import { AfterViewInit, Component, ElementRef, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';

import { I18nService } from '../../../../i18n';
import { ModalService } from '../../../../modal';
import { FormQuestionBaseComponent } from '../../formquestion-base.component';
import { FormFileUploadQuestion } from '../../formquestion-fileupload';

@Component({
    selector: 'form-fileupload-bootstrap',
    templateUrl: './fileupload.component.html',
    styleUrls: ['./fileupload.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class FormFileUploadBootstrapComponent extends FormQuestionBaseComponent implements AfterViewInit {
    @Input() override question: FormFileUploadQuestion;
    @Input() override form: UntypedFormGroup;

    @ViewChild('imagePreview') imagePreview: ElementRef<HTMLImageElement>;

    input: HTMLInputElement;
    label: HTMLElement;

    sysViewUploadedDocument: string;
    sysDownloadUploadedDocument: string;
    sysAcceptedFormats: string;
    sysAnd: string;
    fileSizeReached = false;

    private defaultLabelHtml: string;

    constructor(
        i18nService: I18nService,
        private el: ElementRef,
        private translateService: TranslateService,
        private modalService: ModalService,
    ) {
        super(i18nService);

        this.translateService.setTranslation(
            'fr',
            {
                sysWarning: 'Attention',
                sysMessageUploadLimit: 'Impossible de choisir un fichier excédant la limite permise. ({{sizeLimit}} Ko)',
                sysViewUploadedDocument: 'Consulter le document téléversé',
                sysDownloadUploadedDocument: 'Télécharger le document téléversé',
                sysMessageInvalidFileType: 'Type de fichier invalide. Doit au moins être un des types suivants : {{fileTypes}}',
                sysAcceptedFormats: 'Formats acceptés',
                sysMessageCorruptFile: 'Le fichier téléversé est corrompu. Veuillez téléverser un autre fichier.',
                sysAnd: 'et',
            },
            true,
        );
        this.translateService.setTranslation(
            'en',
            {
                sysWarning: 'Warning',
                sysMessageUploadLimit: 'Can not choose a file exceeding the legal limit. ({{sizeLimit}} Kb)',
                sysViewUploadedDocument: 'View the uploaded document',
                sysDownloadUploadedDocument: 'Download the uploaded document',
                sysMessageInvalidFileType: 'Invalid file type. Must be at least one of the following types: {{fileTypes}}',
                sysAcceptedFormats: 'Accepted formats',
                sysMessageCorruptFile: 'The uploaded file is corrupted. Please upload another file.',
                sysAnd: 'and',
            },
            true,
        );
        const inputs = document.querySelectorAll('.inputfile');
        Array.prototype.forEach.call(inputs, input => {
            /* Must be empty */
        });

        this.sysViewUploadedDocument = this.translateService.instant('sysViewUploadedDocument');
        this.sysDownloadUploadedDocument = this.translateService.instant('sysDownloadUploadedDocument');
        this.sysAcceptedFormats = this.translateService.instant('sysAcceptedFormats');
        this.sysAnd = this.translateService.instant('sysAnd');
    }

    ngAfterViewInit() {
        if (this.question.onAfterViewInit) {
            this.question.onAfterViewInit(this.el.nativeElement);
        }
        super.afterViewInit(this, this.el);

        this.input = (this.el.nativeElement as HTMLElement).getElementsByClassName('form-control')[0] as HTMLInputElement;
        this.label = (this.el.nativeElement as HTMLElement).getElementsByClassName('upload-false-label')[0] as HTMLElement;

        this.defaultLabelHtml = this.label.innerHTML;
        const myThis = this;

        this.imagePreview.nativeElement.onerror = (event: any) => {
            if ((event.target.src as string).indexOf('data:image') > -1) {
                myThis.modalService.alert({
                    title: myThis.translateService.instant('sysWarning'),
                    message: myThis.translateService.instant('sysMessageCorruptFile'),
                });
                myThis.input.value = '';
                myThis.fileSizeReached = true;
                myThis.question.data = null;
                myThis.question.fileName = null;
            }
        };

        // Firefox bug fix
        this.input.addEventListener('focus', () => {
            this.input.classList.add('has-focus');
        });
        this.input.addEventListener('blur', () => {
            this.input.classList.remove('has-focus');
        });
    }

    onFileChange(event: Event) {
        this.fileSizeReached = false;
        const input = event.target as HTMLInputElement;
        const file = input.files != null && input.files.length > 0 ? input.files[0] : null;

        // Size/type validation must run first and return immediately on rejection so that
        // neither the FileReader nor question.onChange (below, or via onChangeInternal) ever
        // sees the rejected file's content.
        if (file != null) {
            if (this.question.sizeLimit !== -1 && file.size > this.question.sizeLimit * 1024) {
                let message = this.translateService.instant('sysMessageUploadLimit', {
                    sizeLimit: this.question.sizeLimit.toString(),
                });

                if (this.question.sizeLimitMessage != null) {
                    const messageExtra = this.translateService.instant(this.question.sizeLimitMessage);
                    message = message + '<br><br>' + messageExtra;
                }

                this.modalService.alert({
                    title: this.translateService.instant('sysWarning'),
                    message: message,
                });
                input.value = '';
                this.fileSizeReached = true;
                this.question.data = null;
                this.question.fileName = null;
                return;
            } else if (this.question.restrictFileTypes && !this.question.restrictFileTypes.some(ext => file.name.endsWith(ext))) {
                this.modalService.alert({
                    title: this.translateService.instant('sysWarning'),
                    message: this.translateService.instant('sysMessageInvalidFileType', { fileTypes: this.question.restrictFileTypes.join(', ') }),
                });
                input.value = '';
                this.fileSizeReached = true;
                this.question.data = null;
                this.question.fileName = null;
                return;
            }
        } else {
            input.value = '';
        }

        let fileName;
        if (input.files && input.files.length > 1) {
            fileName = (input.getAttribute('data-multiple-caption') || '').replace('{count}', input.files.length as any);
        } else {
            fileName = (input.value || '').split('\\').pop();
        }
        if (fileName) {
            this.question.fileName = fileName;
        } else if (this.label) {
            this.label.innerHTML = this.defaultLabelHtml;
        }

        if (file == null) {
            this.question.formControlRef.setValue(null);
            this.question.data = null;

            if (this.question.onChange != null) {
                this.question.onChange(event, this.question);
            }
        } else {
            if (this.question.base64Data) {
                this.question.formControlRef.setValue(file.name);
                const reader: FileReader = new FileReader();

                reader.onload = (readerEvent: Event) => {
                    if (!this.fileSizeReached) {
                        this.question.data = (readerEvent.target as FileReader).result;
                    }

                    if (this.question.onChange != null) {
                        this.question.onChange(event, this.question);
                    }
                };
                reader.readAsDataURL(file);
            } else {
                const formData = new FormData();
                formData.append('file', file);
                this.question.formControlRef.setValue(formData);
                this.question.data = formData;

                if (this.question.onChange != null) {
                    this.question.onChange(event, this.question);
                }
            }
        }
    }

    onReset() {
        this.input.value = '';
        this.label.innerHTML = '';
    }

    /**
     * Kept only because the template still binds to it on the native (change) event, right after onFileChange.
     * The sizeLimit/restrictFileTypes validation and the question.onChange invocation are now both fully
     * handled inside onFileChange itself (which returns before this fires whenever a file is rejected), so
     * this handler intentionally does nothing anymore to avoid double-invoking question.onChange.
     */
    onChangeInternal(event: Event, question: FormFileUploadQuestion) {
        // Intentionally empty - see comment above.
    }

    viewPreviewClick() {
        const win = window.open();
        win.document.write(`
            <iframe src="${this.question.data}" frameborder="0" style="border:0; width:100%; height:100%;" allowfullscreen>
            </iframe>
        `);
        win.document.close();
    }

    downloadPreviewClick() {
        const fileName = this.question.value;
        FileSaver.saveAs(this.question.data, fileName);
    }

    clear() {
        this.question.value = null;
        this.question.data = null;
        this.question.fileName = null;
    }
}
